import { TAO_KOE, EN_KO } from './../libs';
import { showError, hideError, scrollToFirstError } from './../libs/Error';

class FormValidationHandler {
	constructor( formEl ) {
		this.formEl = formEl;

		this.formEl.addEventListener( 'custom_form_submitted.koe', () => this.identifyUser() );
	}

	hideAllErrors() {
		const formEl = this.formEl;
		formEl.querySelectorAll( '.row_has_message' ).forEach( ( rowEl ) => {
			rowEl.querySelectorAll( 'input, select, textarea' ).forEach( ( inputEl ) => hideError( inputEl ) );

			rowEl.classList.remove( 'row_has_message' );
			rowEl.querySelectorAll( '.input_row_message' ).forEach( function( messageEl ) {
				messageEl.style.display = null;
			} );
		} );

		formEl.querySelectorAll( '.success_sent, .fail_sent' ).forEach( ( messageEl ) => {
			EN_KO.functions.toggleDisplay( messageEl, false );
		} );
	}

	setLoading( loading = true ) {
		const formEl = this.formEl;

		const inputRowEl = formEl.querySelector( '.tao_form_button, [type=submit]' )?.closest( '.input_row' ); //needed to access the button in the .done callback
		if ( inputRowEl ) {
			if ( loading ) {
				inputRowEl.setAttribute( 'data-notification', 'loading' );
			} else {
				inputRowEl.removeAttribute( 'data-notification' );
			}
		}
		formEl.classList.toggle( 'is-loading', loading );
	}

	identifyUser() {
		const formEl = this.formEl;
		const spec = formEl.dataset.formType;
		const email = formEl.querySelector( 'input[type=email]' )?.value;
		if ( spec && email ) {
			let event;

			// Validate where the reply comes from to send correct event
			switch ( spec ) {
				case 'pluscard':
					event = 'Pluscard';
					break;
				case 'jobapp':
				case 'gewinnspiel':
					event = formEl.dataset.title;
					break;
				case 'friendinvite':
					event = 'Friend Invite';
					break;
				default:
					event = 'undefined';
					break;
			}

			TAO_KOE.Tracking.sendIdentifyEvent( spec, email, event );
			window.SL_DY_TRACKING.sendIdentifyUser( email );
		}
	}

	postForm() {
		const formEl = this.formEl;
		const method = formEl.method;
		const body = new FormData( formEl );
		body.append( 'ajax', 1 );

		return Promise.resolve().then( () => {
			if ( [ 'GET', 'HEAD' ].includes( method ) ) {
				const params = new URLSearchParams( body );
				return fetch( formEl.action + '?' + params.toString(), {
					method,
				} );
			}

			return fetch( formEl.action, {
				method,
				body,
			} );
		} ).then( async( res ) => {
			try {
				if ( res.ok ) {
					return await res.json();
				}

				return Promise.reject( await res.json() );
			} catch ( err ) {
				console.error( err ); // eslint-disable-line no-console
				return Promise.reject( {
					'code': res.status,
					'status': 'error',
					'message': 'Ein unbekannter Fehler ist aufgetreten!',
				} );
			}
		} );
	}

	async sendForm() {
		const formEl = this.formEl;
		let isValid = true;

		// Should handle frontend validation?
		if ( ! formEl.noValidate ) {
			// Check validity, if function exists.
			isValid = 'checkValidity' in formEl ? formEl.checkValidity() : isValid;
		}

		if ( ! isValid ) {
			return Promise.reject( false );
		}

		if ( formEl.classList.contains( 'is-loading' ) ) {
			return Promise.reject( false );
		}

		// Add Loading State.
		this.setLoading( true );
		this.hideAllErrors();

		return this.postForm().then( ( resp ) => {
			// Form submitted - Fire event, then proceed.
			try {
				this.formEl.dispatchEvent( new CustomEvent( 'custom_form_submitted.koe', { bubbles: true, detail: resp } ) );
			} catch ( err ) {
				console.error( err ); // eslint-disable-line no-console
			}
			return resp;
		} ).then( ( resp ) => {
			if ( 'error' === resp.status ) {
				return Promise.reject( resp );
			}

			if ( 'success' === resp.status ) {
				if ( 'undefined' !== typeof resp.redirect ) {
					window.location.href = resp.redirect;
				} else {
					EN_KO.functions.toggleSlide( formEl.querySelector( '.success_sent' ), true );
					this.resetForm();
				}
			}

			return resp;
		} ).catch( ( resp ) => {
			const invalidEntries = Object.entries( resp.invalid_fields || [] );
			if ( invalidEntries.length > 0 ) {
				const formInputEls = Array.from( formEl.elements );
				invalidEntries.forEach( ( [ key, invalid ] ) => {
					const fieldName = 'string' === typeof invalid ? invalid : key;

					let inputEls = [];
					if ( 'captcha' === fieldName ) {
						const rowEl = formEl.querySelector( '.row_is_captcha' );
						if ( rowEl ) {
							inputEls = rowEl.querySelectorAll( 'input, select, textarea' );
						}
					} else {
						inputEls = formInputEls.filter( ( inputEl ) => inputEl.matches( '[name="' + fieldName + '"]' ) );
					}

					Array.from( inputEls ).forEach( ( inputEl ) => {
						const inputRowEl = inputEl.closest( '.input_row' );
						if ( inputRowEl ) {
							const messageEls = inputRowEl.querySelectorAll( '.input_row_message' );

							if ( messageEls && messageEls.length > 0 ) {
								// May not be needed.
								if ( messageEls.length === 1 ) {
									messageEls.forEach( ( messageEl ) => messageEl.style.display = null );
								}
							} else if ( invalid.message ) {
								inputEl.closest( '.input_row_content' )?.insertAdjacentHTML( 'afterend', `<div class="input_row_message error">${ invalid.message }</div>` );
							}

							showError( inputEl, invalid.message ?? false );
							inputEl.addEventListener( 'input', () => hideError( inputEl ), { once: true } );
							inputEl.addEventListener( 'change', () => hideError( inputEl ), { once: true } );
						}
					} );
				} );

				// Scroll to first error after short delay.
				setTimeout( () => scrollToFirstError( formEl ), 10 );
			} else if ( resp.msg || resp.message ) {
				// Display custom message.
				const errorMessageEl = formEl.querySelector( '.fail_sent:not(.fail_sent--default)' );
				if ( errorMessageEl ) {
					errorMessageEl.innerText = resp.msg || resp.message;
					EN_KO.functions.toggleSlide( errorMessageEl, true );
				}
			} else {
				// Display default message.
				const errorMessageEl = formEl.querySelector( '.fail_sent.fail_sent--default' );
				if ( errorMessageEl ) {
					EN_KO.functions.toggleSlide( errorMessageEl, true );
				}
			}

			return Promise.reject( resp );
		} ).finally( () => {
			// Remove Loading State.
			this.setLoading( false );
		} );
	}

	resetForm() {
		const formEl = this.formEl;
		if ( formEl.classList.contains( 'form--default' ) || [ 'jobapp', 'gewinnspiel', 'sharemail' ].includes( formEl.dataset.formType ) ) {
			// New Reset Way.
			setTimeout( () => formEl.reset(), 10 );
		} else {
			// Old Way.
			Array.from( formEl.elements ).filter( ( inputEl ) => ! inputEl.matches( 'input[type=hidden]' ) ).forEach( ( inputEl ) => {
				if ( inputEl.matches( 'input[type=radio], input[type=checkbox]' ) ) {
					inputEl.checked = false;
				} else {
					inputEl.value = '';
				}
			} );
		}

		if ( 'undefined' !== typeof window.grecaptcha ) {
			formEl.querySelectorAll( '.g-recaptcha' ).forEach( ( captchaEl ) => {
				window.grecaptcha.reset( captchaEl );
			} );
		}
	}

	static getInstance( formEl ) {
		if ( ! formEl || ! formEl.matches( 'form' ) ) {
			return null;
		}

		formEl.KOE_FORM_VALIDATOR ??= new FormValidationHandler( formEl );
		return formEl.KOE_FORM_VALIDATOR;
	}
}

const sendForm = function( evt, formname, callback ) {
	evt.preventDefault();
	evt.stopPropagation();
	evt.stopImmediatePropagation();

	const formEl = ( evt.target ).closest( 'form.' + formname );
	const Validator = FormValidationHandler.getInstance( formEl );
	if ( Validator ) {
		return Validator.sendForm().then( async( resp ) => {
			if ( 'function' === typeof callback ) {
				await callback( resp );
			}
			return resp;
		} ).catch( ( err ) => {
			if ( err ) {
				console.error( '[FormSubmit Error]', formEl, err ); // eslint-disable-line no-console
			}
		} );
	}
	return Promise.reject( 'Invalid form!' );
};

const sendFormElement = function( formEl ) {
	const Validator = FormValidationHandler.getInstance( formEl );
	if ( Validator ) {
		return Validator.sendForm();
	}
	return Promise.reject( 'Invalid form!' );
};

TAO_KOE.ready( 'formvalidation.js', () => {
	// Submit Handler for most forms.
	document.addEventListener( 'submit', ( ev ) => {
		if ( ev.target.matches( 'form.form--default' ) ) {
			ev.preventDefault();
			sendFormElement( ev.target );
		}
	} );

	// Add custom click handler to handle captchas.
	document.addEventListener( 'click', ( evt ) => {
		// Reset Captcha.
		const captchaMessageEl = evt.target.closest( '.row_is_captcha .input_row_message' );
		if ( captchaMessageEl ) {
			if ( 'undefined' !== typeof window.grecaptcha ) {
				const rowEl = evt.target.closest( '.row_is_captcha' );
				window.grecaptcha.reset( rowEl.querySelector( '.g-recaptcha' ) );
				hideError( rowEl );
			}
		}
	} );
} );

TAO_KOE.sendForm = sendForm;
TAO_KOE.sendFormElement = sendFormElement;

export {
	scrollToFirstError,
	sendForm,
	sendFormElement,
};
